const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/sagas/intlSaga.ts';
import moment from 'moment';
import { SagaIterator } from 'redux-saga';
import { put, putResolve, take, select, call, fork } from 'redux-saga/effects';
import { showUncaughtErrorDialog } from '../actions/uiActions';
import { Logger } from '../services/KisLoggerService';
import {
    actionSetI18nLocale,
    actionSetI18nLocaleSuccess,
    atSetI18nLocalePending,
    SetI18nLocalePendingAction,
} from '../actions/intlActions';
import { selLocale } from '../selectors/i18nSelectors';
import { DEFAULT_LOCALE } from '../utils/intlUtils';
import { SupportedLocale } from '../defs/generalDefs';
import { LOAD_PERSDATA_RECORD } from '../actions/userActions';

export const atRehydrate = 'persist/REHYDRATE'; // from redux-persist
const Log = Logger('intlSaga');

async function fetchTranslations(locale: SupportedLocale): Promise<Record<string, string>> {
    const assetsPath = `${window.location.origin}${window.location.pathname.split('/').slice(0, -1).join('/')}/assets`;
    Log.info(`About to load intl dictionary of locale ${locale}, assetsPath is ${assetsPath}`);
    const data = await fetch(`${assetsPath}/translations_${locale}.json`);
    if (data.status !== 200) throw new Error(`HTTP status ${data.status}`);
    return await data.json();
}

function* loadTranslationsSaga(action: SetI18nLocalePendingAction, ignoreCurLocale: boolean): SagaIterator {
    const curLocale = ignoreCurLocale ? undefined : yield select(selLocale);

    if (action.meta.locale == curLocale || action.meta.locale == DEFAULT_LOCALE) {
        // This is trivially resolved.
        yield put(actionSetI18nLocaleSuccess(action.meta.locale, null));
    } else {
        let translations: Record<string, string> | null = null;

        try {
            translations = yield call(fetchTranslations, action.meta.locale);
        } catch (e) {
            Log.error(`Failed to load intl dictionary of locale ${action.meta.locale}`);
        }

        if (translations) {
            yield put(actionSetI18nLocaleSuccess(action.meta.locale, translations));
        }
    }
}

function* setLocaleToUserPrefsSaga(action: any): SagaIterator {
    const branch = action && action.meta && action.meta.branch;
    if (branch !== 'prefs') return; // this is a different kind of user-related data

    const newLocale = action && action.payload && action.payload.CONTENT && action.payload.CONTENT.locale;

    if (newLocale) {
        Log.info(`Setting locale to user-preferred: ${newLocale}`);
        yield put(actionSetI18nLocale(newLocale));
    }
}

function* rehydrateLocaleSaga(action: any): SagaIterator {
    if (!action.payload) {
        Log.error(`rehydrateLocaleSaga: action has no payload!`);
        return;
    }

    const locale = selLocale(action.payload); // payload is the previous state

    if (!locale) {
        Log.warn(`rehydrateLocaleSaga: Cannot determine locale from action!`);
        return;
    }

    Log.info(`Rehydrating locale: ${locale}`);

    // We need to set the locale back to the default first to make sure components will see the update when
    // the translations come in.
    yield putResolve(actionSetI18nLocale(DEFAULT_LOCALE));
    yield call(loadTranslationsSaga, actionSetI18nLocale(locale), true);
}

export function* intlSaga(): SagaIterator {
    // When switching languages, action atSetI18nLocaleSuccess is sent after translations have been loaded, which
    // arrives in intlReducer, which changes moment's locale. The PickersUtilsProvider (and potentially other
    // components) watch the selector selLocale and update themselves accordingly. However, this does not happen when
    // the app is launched. setLocale defaults to 'de', but we must make sure here that moment also defaults to 'de'.
    moment.locale('de');

    try {
        while (true) {
            const action = yield take([atRehydrate, atSetI18nLocalePending, LOAD_PERSDATA_RECORD.success]);

            switch (action.type) {
                case atRehydrate:
                    yield fork(rehydrateLocaleSaga, action);
                    break;
                case atSetI18nLocalePending:
                    yield fork(loadTranslationsSaga, action, false);
                    break;
                case LOAD_PERSDATA_RECORD.success:
                    yield fork(setLocaleToUserPrefsSaga, action);
                    break;
            }
        }
    } catch (e) {
        Log.error('EXCEPTION', typeof e === 'object' && e?.stack);
        yield put(showUncaughtErrorDialog(Log.channel, e));
    }
}
